import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, of, switchMap } from 'rxjs';

import { safeCompare } from '@shared-helpers/string-number-helpers';
import { IHomeBillingEntity, IHomePayrollEntity } from '@shared-interfaces/api';

import { ApiService } from '../../services/api.service';
import { DatahubService, IApplicationQueryParams } from '../../services/datahub.service';

@Component({
  selector: 'home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
  standalone: false,
})
export class HomeComponent implements OnInit {
  private readonly homePageLoadedSubject$ = new BehaviorSubject(false);
  public readonly homePageLoaded$ = this.homePageLoadedSubject$.asObservable();
  public name = '';
  public payrollColumn: IHomePayrollEntity[] = [];
  public billingColumn: IHomeBillingEntity[] = [];

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly datahub: DatahubService,
    private readonly apiService: ApiService,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    this.datahub.switchApprover$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.search();
      },
      error: (err) => {
        console.error('Error switching approver:', err);
      },
    });

    this.activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params) => {
          const typedParams = params as IApplicationQueryParams;
          const approver: string = typedParams.approver ?? this.datahub.user.getApprover();

          if (safeCompare(approver, this.datahub.user.approvingId)) {
            this.search();
            return of(null);
          } else {
            return this.datahub.user.switchApprover$(approver).pipe(
              switchMap(() => {
                this.search();
                return of(null);
              }),
              takeUntilDestroyed(this.destroyRef),
            );
          }
        }),
      )
      .subscribe({
        error: (err) => {
          console.error('Error switching approver or during queryParams handling:', err);
        },
      });
  }

  public get currentQueryParams(): IApplicationQueryParams {
    return { wedate: this.datahub.weDate.getSapWeDateString(), approver: this.datahub.user.approvingId };
  }

  private search(): void {
    this.homePageLoadedSubject$.next(false);
    this.datahub.updateQueryParams();
    this.name = this.datahub.user.approvingName;
    this.apiService.getHomePage$(this.datahub.user.approvingId).subscribe({
      next: (data) => {
        this.payrollColumn = data.payrollColumn;
        this.billingColumn = data.billingColumn;
        this.homePageLoadedSubject$.next(true);
      },
      error: (err) => {
        console.error('Error fetching home page data:', err);
      },
    });
  }
}
